import { z } from 'zod';
import type { RecommendationType } from '../enums';

export const RECOMMENDATION_IMPORT_FORMATS = [
  'trustee_form',
  'internal_form',
  'g_family_sheet',
  'current_family_bot_sheet',
] as const;
export const RecommendationImportFormat = z.enum(RECOMMENDATION_IMPORT_FORMATS);
export type RecommendationImportFormat = z.infer<typeof RecommendationImportFormat>;

export type ImportField =
  | 'submittedAt'
  | 'recommenderName'
  | 'familyLastName'
  | 'familyDetails'
  | 'connection'
  | 'valueAdd'
  | 'academicStanding'
  | 'disciplinaryStanding'
  | 'willEnroll'
  | 'additionalInfo'
  | 'childFirstName'
  | 'gradeApplying'
  | 'constituentId'
  | 'constituentName'
  | 'notes'
  | 'lifetimeGiving'
  | 'capacityRaw'
  | 'relatedChildren'
  | 'relatedApplicant'
  | 'solicitor1'
  | 'solicitor2';

export interface ImportColumnMatcher {
  field: ImportField;
  match: readonly string[];
}

export interface ImportExtraColumn {
  label: string;
  match: readonly string[];
}

export interface RecommendationImportFormatDef {
  format: RecommendationImportFormat;
  label: string;
  recommendationType: RecommendationType;
  columns: readonly ImportColumnMatcher[];
  extras: readonly ImportExtraColumn[];
  requiredFields: readonly ImportField[];
  positional?: readonly ImportField[];
}

const FORM_COLUMNS: readonly ImportColumnMatcher[] = [
  { field: 'submittedAt', match: ['timestamp'] },
  { field: 'recommenderName', match: ['your first and last name', 'first and last name'] },
  { field: 'familyLastName', match: ['recommended family last name', 'family last name'] },
  { field: 'familyDetails', match: ['names of parents'] },
  { field: 'connection', match: ['how you are connected'] },
  { field: 'valueAdd', match: ['add value'] },
  { field: 'academicStanding', match: ['academic standing'] },
  { field: 'disciplinaryStanding', match: ['disciplinary standing'] },
  { field: 'willEnroll', match: ['will the family enroll'] },
  { field: 'additionalInfo', match: ['additional information'] },
];

const FORM_EXTRAS: readonly ImportExtraColumn[] = [
  { label: 'Admissions notes', match: ['hess notes'] },
  { label: 'Development outreach', match: ['development outreach'] },
];

export const RECOMMENDATION_IMPORT_FORMAT_DEFS: Record<
  RecommendationImportFormat,
  RecommendationImportFormatDef
> = {
  trustee_form: {
    format: 'trustee_form',
    label: 'Trustee Recommendation Form responses (Google Form export)',
    recommendationType: 'trustee',
    columns: FORM_COLUMNS,
    extras: FORM_EXTRAS,
    requiredFields: ['recommenderName', 'familyLastName'],
  },
  internal_form: {
    format: 'internal_form',
    label: 'Internal Recommendation Form responses (Google Form export)',
    recommendationType: 'internal',
    columns: FORM_COLUMNS,
    extras: FORM_EXTRAS,
    requiredFields: ['recommenderName', 'familyLastName'],
  },
  g_family_sheet: {
    format: 'g_family_sheet',
    label: 'Recommended by G Families (Family, Child, Grade, Recommender)',
    recommendationType: 'g_family',
    columns: [
      { field: 'familyLastName', match: ['family'] },
      { field: 'childFirstName', match: ['child', 'applicant'] },
      { field: 'gradeApplying', match: ['grade'] },
      { field: 'recommenderName', match: ['recommender', 'recommended by'] },
    ],
    extras: [],
    requiredFields: ['familyLastName', 'recommenderName'],
    positional: ['familyLastName', 'childFirstName', 'gradeApplying', 'recommenderName'],
  },
  current_family_bot_sheet: {
    format: 'current_family_bot_sheet',
    label: 'Current Families & BOT Rec (Veracross Development export)',
    recommendationType: 'current_family_bot',
    columns: [
      { field: 'constituentId', match: ['constituent id'] },
      { field: 'constituentName', match: ['constituent'] },
      { field: 'notes', match: ['notes'] },
      { field: 'lifetimeGiving', match: ['lifetime giving'] },
      { field: 'capacityRaw', match: ['overall capacity', 'capacity'] },
      { field: 'relatedChildren', match: ['related children'] },
      { field: 'relatedApplicant', match: ['related aplicant', 'related applicant'] },
      { field: 'solicitor1', match: ['solicitor 1'] },
      { field: 'solicitor2', match: ['solicitor 2'] },
    ],
    extras: [],
    requiredFields: ['constituentName'],
  },
};

const normHeader = (h: string) => h.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\s+/g, ' ');

export interface ImportHeaderMapping {
  indexByField: Partial<Record<ImportField, number>>;
  extras: { label: string; index: number }[];
  ignoredHeaders: string[];
  missingRequired: ImportField[];
}

export function mapImportHeaders(
  def: RecommendationImportFormatDef,
  headers: readonly string[],
): ImportHeaderMapping {
  const indexByField: Partial<Record<ImportField, number>> = {};
  const extras: { label: string; index: number }[] = [];
  const ignoredHeaders: string[] = [];
  const exact = (h: string, m: string) => h === m;
  const contains = (h: string, m: string) => h.includes(m);

  const assigned = new Set<number>();
  for (const pass of [exact, contains]) {
    for (const col of def.columns) {
      if (indexByField[col.field] !== undefined) continue;
      const idx = headers.findIndex(
        (raw, i) => !assigned.has(i) && col.match.some((m) => pass(normHeader(raw), m)),
      );
      if (idx >= 0) {
        indexByField[col.field] = idx;
        assigned.add(idx);
      }
    }
  }
  headers.forEach((raw, i) => {
    if (assigned.has(i)) return;
    const extra = def.extras.find((e) => e.match.some((m) => normHeader(raw).includes(m)));
    if (extra) {
      extras.push({ label: extra.label, index: i });
      return;
    }
    if (raw.trim()) ignoredHeaders.push(raw.trim());
  });

  const missingRequired = def.requiredFields.filter((f) => indexByField[f] === undefined);
  return { indexByField, extras, ignoredHeaders, missingRequired };
}

export function positionalMapping(def: RecommendationImportFormatDef): ImportHeaderMapping {
  const indexByField: Partial<Record<ImportField, number>> = {};
  (def.positional ?? []).forEach((field, i) => {
    indexByField[field] = i;
  });
  const missingRequired = def.requiredFields.filter((f) => indexByField[f] === undefined);
  return { indexByField, extras: [], ignoredHeaders: [], missingRequired };
}

export function familyLastNameFromConstituent(name: string): string {
  const v = name.trim();
  if (!v) return v;
  if (v.includes(',')) return v.split(',')[0]!.trim();
  const cleaned = v
    .replace(/\b(mr|mrs|ms|dr|and|&|the|family)\b\.?/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const parts = cleaned.split(' ');
  return parts[parts.length - 1] ?? v;
}
